import { useState } from 'react';

import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Option from '@/components/Option';
import Button from '@/components/Button';
import TextInput from '@/components/TextInput';
import styled from '@/styles';
import { GetJobQuery } from '@/types/graphql';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { JobQualityGateData } from '@/services/jobsAPI';

type Props = {
  job: GetJobQuery['job'];
  qualityGate?: JobQualityGateData;
  onUpdated?: () => void;
};

const SectionTitle = styled('p', {
  fontSize: '13px',
  fontWeight: 700,
  color: '#1E2D4D',
  marginBottom: '4px',
});

const QualityGateSection = ({ job, qualityGate, onUpdated }: Props) => {
  const customerGateEnabled =
    qualityGate?.customer_quality_gate_enabled ?? false;
  const isOverridden = qualityGate?.quality_gate_disabled ?? false;
  const gateActive = customerGateEnabled && !isOverridden;

  const [overrideEnabled, setOverrideEnabled] = useState(isOverridden);
  const [reason, setReason] = useState(
    qualityGate?.quality_gate_disabled_reason || '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasChanged = overrideEnabled !== isOverridden;

  const handleSave = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await axiosRubyClient.post(
        `/api/admin/jobs/${job.id}/quality_gate_override`,
        {
          quality_gate_disabled: overrideEnabled,
          reason: overrideEnabled ? reason : undefined,
        },
      );
      onUpdated?.();
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] || 'Failed to update override';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '10px 20px' }}>
      <SectionTitle>Dependability Gate</SectionTitle>

      <Stack vertical gap={8}>
        <Small>
          {customerGateEnabled
            ? gateActive
              ? 'Active — only high-reliability workers can access this job'
              : 'Overridden — gate disabled for this shift'
            : 'Disabled at customer level'}
        </Small>

        {customerGateEnabled && (
          <>
            <Option
              id="quality-gate-override"
              appearance="switch"
              checked={overrideEnabled}
              label="Override: disable gate for this shift"
              onChange={({ currentTarget }) =>
                setOverrideEnabled(currentTarget.checked)
              }
            />

            {overrideEnabled && (
              <TextInput
                placeholder="Reason for override (required)"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
            )}

            {error && <Small color="danger">{error}</Small>}

            {hasChanged && (
              <Button
                a11yLabel="Save quality gate override"
                isLoading={isLoading}
                label="Save Override"
                appearance="primary"
                onClick={handleSave}
              />
            )}
          </>
        )}
      </Stack>
    </div>
  );
};

export default QualityGateSection;
